import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  ShieldHalf, Building2, Users as UsersIcon, DollarSign, TrendingUp, Plus, LogOut,
  Loader2, Search, Trash2, Layers, Check, Mic, CreditCard, Palette, Download, RefreshCw,
  CalendarCheck, Sparkles, KanbanSquare, MessageSquareText, KeyRound, Inbox as InboxIcon,
  HandCoins, Newspaper, Bell, CalendarClock, LifeBuoy, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { platformApi, platformInboxApi, formsApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import CompanyDrawer from "../components/platform/CompanyDrawer";
import PlatformStripeCard from "../components/platform/PlatformStripeCard";
import PlatformTwilioCard from "../components/platform/PlatformTwilioCard";
import VoiceEngineCard from "../components/platform/VoiceEngineCard";
import BrandingPanel from "../components/platform/BrandingPanel";
import CreditsAdmin from "../components/platform/CreditsAdmin";
import PlatformCRM from "../components/platform/PlatformCRM";
import PlatformPopups from "../components/platform/PlatformPopups";
import PlatformDemoBooking from "../components/platform/PlatformDemoBooking";
import PlatformCalendar from "../components/platform/PlatformCalendar";
import DemoAccountsPanel from "./platform/DemoAccountsPanel";
import SeoResourcesPanel from "../components/platform/SeoResourcesPanel";
import AffiliatesAdmin from "../components/platform/AffiliatesAdmin";
import PlatformHelpCenter from "../components/platform/PlatformHelpCenter";
import Inbox from "./Inbox";
import { Modal, DInput, DSelect, money2, STATUS_PILL } from "../components/platform/ui";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function PlatformAdmin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("companies");
  const [metrics, setMetrics] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [showProvision, setShowProvision] = useState(false);
  const [editPlan, setEditPlan] = useState(null); // plan obj or {} for new
  const [voiceUsage, setVoiceUsage] = useState(null);
  const [runningRenewals, setRunningRenewals] = useState(false);
  const [demoRequests, setDemoRequests] = useState([]);

  const doRunRenewals = async () => {
    setRunningRenewals(true);
    try {
      const res = await platformApi.runRenewals();
      toast.success(res.generated > 0 ? `${res.generated} renewal invoice(s) generated.` : "No companies are due for renewal.");
      await reload();
    } catch (e) {
      toast.error("Could not run renewals.");
    } finally {
      setRunningRenewals(false);
    }
  };

  const reload = useCallback(async () => {
    try {
      const [m, c, p, v] = await Promise.all([
        platformApi.metrics(), platformApi.listCompanies(), platformApi.listPlans(), platformApi.voiceUsage().catch(() => null),
      ]);
      setMetrics(m); setCompanies(c); setPlans(p); setVoiceUsage(v);
      platformApi.listDemoRequests().then(setDemoRequests).catch(() => {});
    } catch (_) {
      toast.error("Failed to load platform data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!(user && user !== false && user.role === "super_admin")) return;
    let active = true;
    (async () => { await Promise.resolve(); if (active) reload(); })();
    return () => { active = false; };
  }, [user, reload]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0E17]"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>;
  }
  if (user === false || user.role !== "super_admin") {
    return <Navigate to="/platform/login" replace />;
  }

  const filtered = companies.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.ownerEmail || "").includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0E17] text-slate-200">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0A0E17]/90 backdrop-blur">
        <div className="px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center">
              <ShieldHalf className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-white font-bold text-[15px] leading-tight">Platform Console</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">ServiceGuru Pro</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[12.5px] text-slate-400 hidden sm:block">{user.email}</span>
            <button
              data-testid="platform-logout"
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-[12.5px] text-slate-300 hover:bg-white/5"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 py-6 max-w-7xl mx-auto">
        {/* Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="platform-metrics">
          <Metric icon={Building2} label="Companies" value={metrics?.totalCompanies ?? "—"} accent="from-cyan-500/20 to-cyan-500/5" iconCls="text-cyan-400" />
          <Metric icon={UsersIcon} label="Total users" value={metrics?.totalUsers ?? "—"} accent="from-blue-500/20 to-blue-500/5" iconCls="text-blue-400" />
          <Metric icon={DollarSign} label="MRR" value={metrics ? money(metrics.mrr) : "—"} accent="from-emerald-500/20 to-emerald-500/5" iconCls="text-emerald-400" />
          <Metric icon={TrendingUp} label="ARR" value={metrics ? money(metrics.arr) : "—"} accent="from-violet-500/20 to-violet-500/5" iconCls="text-violet-400" />
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="inline-flex rounded-lg bg-white/5 p-1 border border-white/10">
            {[["companies", "Companies", Building2], ["crm", "CRM", KanbanSquare], ["inbox", "Inbox", InboxIcon], ["calendar", "Calendar", CalendarDays], ["popups", "Popups", Bell], ["plans", "Plans", Layers], ["affiliates", "Affiliates", HandCoins], ["voice", "Voice AI", Mic], ["credits", "AI Credits", Sparkles], ["payments", "Payments", CreditCard], ["twilio", "Twilio", MessageSquareText], ["demos", "Demos", CalendarCheck], ["demo-booking", "Demo Booking", CalendarClock], ["demo-accounts", "Demo Accounts", KeyRound], ["seo", "SEO Resources", Newspaper], ["branding", "Branding", Palette], ["help-center", "Help Center", LifeBuoy]].map(([key, label, Icon]) => (
              <button
                key={key} data-testid={`platform-tab-${key}`}
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${tab === key ? "bg-cyan-500/20 text-cyan-200" : "text-slate-400 hover:text-slate-200"}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          {tab === "companies" ? (
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="platform-search"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search companies…"
                  className="h-9 w-56 pl-9 pr-3 rounded-lg bg-white/5 border border-white/10 text-[13px] text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                />
              </div>
              <a
                data-testid="platform-export-csv"
                href={platformApi.mrrExportUrl()}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px] font-medium"
                title="Download billing summary CSV (MRR) for bookkeeping"
              >
                <Download className="h-4 w-4" /> Export CSV
              </a>
              <button
                data-testid="platform-run-renewals"
                onClick={doRunRenewals}
                disabled={runningRenewals}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px] font-medium disabled:opacity-50"
                title="Generate renewal invoices for auto-pay companies whose cycle has ended"
              >
                {runningRenewals ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Run renewals
              </button>
              <button
                data-testid="platform-provision-btn"
                onClick={() => setShowProvision(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold"
              >
                <Plus className="h-4 w-4" /> Provision company
              </button>
            </div>
          ) : tab === "plans" ? (
            <button
              data-testid="platform-new-plan-btn"
              onClick={() => setEditPlan({})}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold"
            >
              <Plus className="h-4 w-4" /> New plan
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-cyan-400 animate-spin" /></div>
        ) : tab === "companies" ? (
          <CompaniesTable companies={filtered} onOpen={setDetailId} />
        ) : tab === "crm" ? (
          <PlatformCRM />
        ) : tab === "inbox" ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden flex" style={{ height: "calc(100vh - 260px)", minHeight: 520 }} data-testid="platform-inbox-panel">
            <Inbox api={platformInboxApi} platformMode />
          </div>
        ) : tab === "popups" ? (
          <PlatformPopups />
        ) : tab === "calendar" ? (
          <PlatformCalendar />
        ) : tab === "voice" ? (
          <div className="space-y-5">
            <VoiceEngineCard />
            <VoiceUsageTable data={voiceUsage} onRatesSaved={(rates) => setVoiceUsage((v) => ({ ...(v || {}), rates }))} />
          </div>
        ) : tab === "affiliates" ? (
          <AffiliatesAdmin />
        ) : tab === "credits" ? (
          <CreditsAdmin />
        ) : tab === "payments" ? (
          <PlatformStripeCard />
        ) : tab === "twilio" ? (
          <PlatformTwilioCard />
        ) : tab === "demos" ? (
          <DemoRequestsTable requests={demoRequests} onChange={reload} />
        ) : tab === "demo-booking" ? (
          <PlatformDemoBooking />
        ) : tab === "demo-accounts" ? (
          <DemoAccountsPanel />
        ) : tab === "seo" ? (
          <SeoResourcesPanel />
        ) : tab === "branding" ? (
          <BrandingPanel />
        ) : tab === "help-center" ? (
          <PlatformHelpCenter />
        ) : (
          <PlansGrid plans={plans} onEdit={setEditPlan} onDelete={async (id) => {
            try { await platformApi.deletePlan(id); toast.success("Plan deleted."); reload(); }
            catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
          }} />
        )}
      </div>

      {showProvision && (
        <ProvisionModal plans={plans} onClose={() => setShowProvision(false)} onDone={() => { setShowProvision(false); reload(); }} />
      )}
      {detailId && (
        <CompanyDrawer id={detailId} plans={plans} onClose={() => setDetailId(null)} onChange={reload} />
      )}
      {editPlan && (
        <PlanModal plan={editPlan} onClose={() => setEditPlan(null)} onDone={() => { setEditPlan(null); reload(); }} />
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent, iconCls }) {
  return (
    <div className={`rounded-xl border border-white/10 bg-gradient-to-br ${accent} p-4`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
        <Icon className={`h-4 w-4 ${iconCls}`} />
      </div>
      <div className="text-[24px] font-bold text-white">{value}</div>
    </div>
  );
}

function RateField({ k, label, suffix, value, onChange }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <div className="flex items-center gap-1.5">
        <span className="text-slate-500 text-[13px]">$</span>
        <input
          data-testid={`ai-rate-${k}`}
          type="number" step="0.001" min="0"
          value={value} onChange={onChange}
          className="h-9 w-28 px-2.5 rounded-lg bg-white/5 border border-white/10 text-[13px] text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
        />
        <span className="text-slate-500 text-[12px]">{suffix}</span>
      </div>
    </div>
  );
}

function AiRatesEditor({ rates, onSaved }) {
  const [form, setForm] = useState(rates || null);
  const [saving, setSaving] = useState(false);
  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        promptPer1M: parseFloat(form.promptPer1M),
        completionPer1M: parseFloat(form.completionPer1M),
        whisperPerMin: parseFloat(form.whisperPerMin),
        callPerMin: parseFloat(form.callPerMin),
      };
      const next = await platformApi.updateAiRates(payload);
      toast.success("AI base rates updated.");
      onSaved?.(next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save rates.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-4" data-testid="ai-rates-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[13.5px] font-semibold text-slate-200">AI base cost rates</div>
          <div className="text-[11.5px] text-slate-500">Your underlying provider cost — used to compute every tenant&apos;s billable overage.</div>
        </div>
        <button
          data-testid="ai-rates-save"
          onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-cyan-500/20 text-cyan-200 text-[13px] font-semibold hover:bg-cyan-500/30 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save rates
        </button>
      </div>
      <div className="flex flex-wrap gap-5">
        <RateField k="promptPer1M" label="Prompt tokens" suffix="/ 1M" value={form.promptPer1M} onChange={set("promptPer1M")} />
        <RateField k="completionPer1M" label="Completion tokens" suffix="/ 1M" value={form.completionPer1M} onChange={set("completionPer1M")} />
        <RateField k="whisperPerMin" label="Whisper audio" suffix="/ min" value={form.whisperPerMin} onChange={set("whisperPerMin")} />
        <RateField k="callPerMin" label="AI voice call" suffix="/ min" value={form.callPerMin} onChange={set("callPerMin")} />
      </div>
    </div>
  );
}

function VoiceUsageTable({ data, onRatesSaved }) {
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const num = (n) => Number(n || 0).toLocaleString();
  const usd = (n, d = 2) => `$${Number(n || 0).toFixed(d)}`;
  return (
    <div data-testid="platform-voice-usage">
      <AiRatesEditor key={data?.rates ? "loaded" : "init"} rates={data?.rates} onSaved={onRatesSaved} />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Metric icon={Mic} label="Voice requests" value={num(totals.llmRequests)} accent="from-cyan-500/20 to-cyan-500/5" iconCls="text-cyan-400" />
        <Metric icon={DollarSign} label="Real AI cost (lifetime)" value={usd(totals.estCostUsd, 4)} accent="from-blue-500/20 to-blue-500/5" iconCls="text-blue-400" />
        <Metric icon={CreditCard} label="Audio minutes (lifetime)" value={num(totals.audioMinutes)} accent="from-violet-500/20 to-violet-500/5" iconCls="text-violet-400" />
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-slate-400 border-b border-white/10 text-[11px] uppercase tracking-wider">
                <th className="px-4 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold text-right">Total tokens</th>
                <th className="px-4 py-3 font-semibold text-right">Audio min</th>
                <th className="px-4 py-3 font-semibold text-right">Call min</th>
                <th className="px-4 py-3 font-semibold text-right">Real AI cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">No voice usage recorded yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.companyId} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`voice-usage-row-${r.companyId}`}>
                  <td className="px-4 py-3 font-semibold text-slate-200">
                    {r.companyName}
                    {r.planName && <span className="ml-2 text-[10.5px] text-slate-500 uppercase tracking-wider">{r.planName}</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-300">{num(r.totalTokens)}</td>
                  <td className="px-4 py-3 text-right text-slate-300">{num(r.audioMinutes)}</td>
                  <td className="px-4 py-3 text-right text-slate-300">{num(r.callMinutes)}</td>
                  <td className="px-4 py-3 text-right text-cyan-300 font-semibold" data-testid={`voice-usage-cost-${r.companyId}`}>{usd(r.estCostUsd, 4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mt-2">
        Lifetime usage &amp; real AI cost per company, priced at the base rates above — informational only. Billing happens live via each company's prepaid AI-credit wallet at time of use (see the AI Credits tab for actual credits spent + platform margin).
      </div>
    </div>
  );
}

const DEMO_STATUS = ["new", "contacted", "scheduled", "done"];
const DEMO_STATUS_CLS = {
  new: "bg-amber-500/20 text-amber-300", contacted: "bg-blue-500/20 text-blue-300",
  scheduled: "bg-violet-500/20 text-violet-300", done: "bg-emerald-500/20 text-emerald-300",
};

function DemoRequestsTable({ requests, onChange }) {
  const setStatus = async (id, status) => {
    try { await platformApi.updateDemoRequest(id, { status }); onChange(); }
    catch (e) { toast.error("Could not update status."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this demo request?")) return;
    try { await platformApi.deleteDemoRequest(id); toast.success("Demo request deleted."); onChange(); }
    catch (e) { toast.error("Could not delete."); }
  };
  if (!requests.length) {
    return <div className="rounded-xl border border-white/10 bg-white/[0.02] py-16 text-center text-slate-500 text-[13.5px]" data-testid="platform-demos-empty">No demo requests yet. Submissions from the landing page appear here.</div>;
  }
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="platform-demos-table">
      <table className="w-full text-left min-w-[860px]">
        <thead>
          <tr className="text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-white/10">
            <th className="px-4 py-3">Requested</th>
            <th className="px-4 py-3">Contact</th>
            <th className="px-4 py-3">Preferred</th>
            <th className="px-4 py-3">Notes</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="text-[13px] text-slate-300">
          {requests.map((r) => (
            <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`demo-row-${r.id}`}>
              <td className="px-4 py-3 whitespace-nowrap text-slate-400">{r.createdAt ? new Date(r.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</td>
              <td className="px-4 py-3">
                <div className="font-semibold text-slate-100">{r.name}</div>
                {r.company ? <div className="text-[11.5px] text-slate-500">{r.company}</div> : null}
                <div className="text-[11.5px] text-cyan-300/80">{r.email}</div>
                {r.phone ? <div className="text-[11.5px] text-slate-500">{r.phone}</div> : null}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-slate-400">{[r.preferredDate, r.preferredTime].filter(Boolean).join(" · ") || "—"}</td>
              <td className="px-4 py-3 max-w-[240px]"><div className="text-slate-400 line-clamp-2">{r.notes || "—"}</div></td>
              <td className="px-4 py-3">
                <select
                  data-testid={`demo-status-${r.id}`}
                  value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}
                  className={`rounded-md px-2 py-1 text-[11.5px] font-semibold capitalize border-none focus:outline-none focus:ring-2 focus:ring-cyan-500/30 ${DEMO_STATUS_CLS[r.status] || DEMO_STATUS_CLS.new}`}
                >
                  {DEMO_STATUS.map((s) => <option key={s} value={s} className="bg-slate-800 text-slate-100">{s}</option>)}
                </select>
              </td>
              <td className="px-4 py-3 text-right">
                <button data-testid={`demo-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded text-rose-300 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function CompaniesTable({ companies, onOpen }) {
  if (!companies.length) {
    return <div className="rounded-xl border border-white/10 bg-white/[0.02] py-16 text-center text-slate-500 text-[13.5px]">No companies found.</div>;
  }
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="platform-companies-table">
      <table className="w-full text-left">
        <thead>
          <tr className="text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-white/10">
            <th className="px-4 py-3 font-semibold">Company</th>
            <th className="px-4 py-3 font-semibold">Plan</th>
            <th className="px-4 py-3 font-semibold">Seats / Users</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold text-right">MRR</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((c) => (
            <tr
              key={c.id}
              data-testid={`platform-company-row-${c.id}`}
              onClick={() => onOpen(c.id)}
              className="border-b border-white/[0.06] last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors"
            >
              <td className="px-4 py-3">
                <div className="text-[13.5px] font-semibold text-slate-100">{c.name}</div>
                <div className="text-[11.5px] text-slate-500">{c.ownerEmail}</div>
              </td>
              <td className="px-4 py-3 text-[13px] text-slate-300">{c.planName || "—"}<span className="text-slate-500 text-[11px]"> · {c.term}</span></td>
              <td className="px-4 py-3 text-[13px] text-slate-300">{c.seats} / {c.userCount}</td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2.5 py-0.5 rounded-full border text-[11px] font-semibold capitalize ${STATUS_PILL[c.subscriptionStatus] || STATUS_PILL.canceled}`}>
                  {c.subscriptionStatus.replace("_", " ")}
                </span>
              </td>
              <td className="px-4 py-3 text-right text-[13.5px] font-semibold text-emerald-300">{money2(c.mrr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlansGrid({ plans, onEdit, onDelete }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="platform-plans-grid">
      {plans.map((p) => (
        <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[15px] font-bold text-white">{p.name}</div>
              <div className="text-[11px] text-slate-500">{p.active ? "Active" : "Inactive"} · min {p.minSeats} seats</div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid={`plan-edit-${p.id}`} onClick={() => onEdit(p)} className="px-2 py-1 rounded text-[11.5px] text-cyan-300 hover:bg-white/5">Edit</button>
              <button data-testid={`plan-delete-${p.id}`} onClick={() => onDelete(p.id)} className="p-1.5 rounded text-rose-300 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          <div className="flex items-baseline gap-1.5 mb-3">
            <span className="text-[22px] font-bold text-white">{money2(p.perSeatMonthly)}</span>
            <span className="text-[12px] text-slate-500">/seat/mo</span>
            <span className="text-[12px] text-slate-500 ml-2">· {money2(p.perSeatAnnual)}/yr</span>
          </div>
          {p.description && <p className="text-[12px] text-slate-400 mb-2 line-clamp-2">{p.description}</p>}
          <ul className="space-y-1.5">
            {(p.features || []).map((f) => (
              <li key={f} className="flex items-center gap-2 text-[12.5px] text-slate-400"><Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> {f}</li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap text-[11px]">
            <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20" data-testid={`plan-modules-count-${p.id}`}>
              {(p.modules || []).length} module{(p.modules || []).length === 1 ? "" : "s"}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
              AI: ${Number(p.aiIncludedUsd || 0).toFixed(0)} incl · {Number(p.aiMarkupPct || 0).toFixed(0)}% markup
            </span>
            {p.onboardingPrice > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" data-testid={`plan-onboarding-price-${p.id}`}>
                Onboarding: {money2(p.onboardingPrice)}{p.onboardingIntakeFormId ? " · intake form set" : ""}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Provision modal ----------
function ProvisionModal({ plans, onClose, onDone }) {
  const [f, setF] = useState({ name: "", ownerName: "", ownerEmail: "", ownerPassword: "", contactPhone: "", planId: plans[0]?.id || "", term: "monthly", seats: 1, trialDays: 14 });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await platformApi.createCompany({
        name: f.name.trim(), ownerName: f.ownerName.trim() || null, ownerEmail: f.ownerEmail.trim().toLowerCase(),
        ownerPassword: f.ownerPassword, contactPhone: f.contactPhone.trim(), planId: f.planId, term: f.term,
        seats: Number(f.seats) || 1, trialDays: Number(f.trialDays) || 0,
      });
      toast.success("Company provisioned.");
      onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message); setBusy(false);
    }
  };

  return (
    <Modal title="Provision new company" onClose={onClose} testid="platform-provision-modal">
      <form onSubmit={submit} className="space-y-3.5">
        <DInput label="Company name" value={f.name} onChange={set("name")} required testid="provision-name" />
        <div className="grid grid-cols-2 gap-3">
          <DInput label="Owner name" value={f.ownerName} onChange={set("ownerName")} testid="provision-owner-name" />
          <DInput label="Phone" value={f.contactPhone} onChange={set("contactPhone")} testid="provision-phone" />
        </div>
        <DInput label="Owner email" type="email" value={f.ownerEmail} onChange={set("ownerEmail")} required testid="provision-email" />
        <DInput label="Temp password" type="text" value={f.ownerPassword} onChange={set("ownerPassword")} required testid="provision-password" />
        <div className="grid grid-cols-3 gap-3">
          <DSelect label="Plan" value={f.planId} onChange={set("planId")} testid="provision-plan" options={plans.map((p) => [p.id, p.name])} />
          <DSelect label="Term" value={f.term} onChange={set("term")} testid="provision-term" options={[["monthly", "Monthly"], ["annual", "Annual"]]} />
          <DInput label="Seats" type="number" value={f.seats} onChange={set("seats")} testid="provision-seats" />
        </div>
        <DInput label="Trial days" type="number" value={f.trialDays} onChange={set("trialDays")} testid="provision-trial-days" />
        {error && <div className="px-3 py-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-[12.5px] text-rose-300">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] text-slate-300 hover:bg-white/5">Cancel</button>
          <button type="submit" disabled={busy} data-testid="provision-submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[13px] font-semibold disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Provision
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Plan modal ----------
function PlanModal({ plan, onClose, onDone }) {
  const isNew = !plan.id;
  const [f, setF] = useState({
    name: plan.name || "", description: plan.description || "",
    perSeatMonthly: plan.perSeatMonthly ?? 0, perSeatAnnual: plan.perSeatAnnual ?? 0,
    minSeats: plan.minSeats ?? 1, features: (plan.features || []).join("\n"), active: plan.active ?? true,
    modules: plan.modules || [], aiMarkupPct: plan.aiMarkupPct ?? 0, aiIncludedUsd: plan.aiIncludedUsd ?? 0,
    onboardingPrice: plan.onboardingPrice ?? 0, onboardingIntakeFormId: plan.onboardingIntakeFormId || "",
  });
  const [catalog, setCatalog] = useState({ core: [], gateable: [] });
  const [forms, setForms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    let active = true;
    (async () => {
      await Promise.resolve();
      try {
        const c = await platformApi.modules();
        if (active) setCatalog(c);
      } catch { /* ignore */ }
      try {
        const fl = await formsApi.list();
        if (active) setForms((fl || []).filter((x) => x.status === "active"));
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, []);

  const toggleModule = (key) =>
    setF((s) => ({ ...s, modules: s.modules.includes(key) ? s.modules.filter((m) => m !== key) : [...s.modules, key] }));

  // Group gateable modules by their group label for a tidy checklist.
  const groups = {};
  (catalog.gateable || []).forEach((m) => { (groups[m.group] = groups[m.group] || []).push(m); });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const body = {
      name: f.name.trim(), perSeatMonthly: Number(f.perSeatMonthly) || 0, perSeatAnnual: Number(f.perSeatAnnual) || 0,
      minSeats: Number(f.minSeats) || 1, features: f.features.split("\n").map((x) => x.trim()).filter(Boolean), active: !!f.active,
      modules: f.modules, aiMarkupPct: Number(f.aiMarkupPct) || 0, aiIncludedUsd: Number(f.aiIncludedUsd) || 0,
      description: f.description.trim(), onboardingPrice: Number(f.onboardingPrice) || 0,
      onboardingIntakeFormId: f.onboardingIntakeFormId || null,
    };
    try {
      if (isNew) await platformApi.createPlan(body);
      else await platformApi.updatePlan(plan.id, body);
      toast.success(isNew ? "Plan created." : "Plan updated.");
      onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message); setBusy(false);
    }
  };

  return (
    <Modal title={isNew ? "New plan" : `Edit ${plan.name}`} onClose={onClose} testid="platform-plan-modal">
      <form onSubmit={submit} className="space-y-3.5">
        <DInput label="Plan name" value={f.name} onChange={set("name")} required testid="plan-name" />
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Description</label>
          <textarea value={f.description} onChange={set("description")} rows={2} data-testid="plan-description"
            placeholder="Shown on the Done for You Onboarding plan card"
            className="w-full px-3 py-2 rounded-lg bg-[#0F1623] border border-white/10 text-[13px] text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <DInput label="$/seat/mo" type="number" value={f.perSeatMonthly} onChange={set("perSeatMonthly")} testid="plan-monthly" />
          <DInput label="$/seat/yr" type="number" value={f.perSeatAnnual} onChange={set("perSeatAnnual")} testid="plan-annual" />
          <DInput label="Min seats" type="number" value={f.minSeats} onChange={set("minSeats")} testid="plan-min-seats" />
        </div>

        {/* Done for You Onboarding — one-time purchase settings */}
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
          <div className="text-[11.5px] font-semibold text-emerald-200 mb-2 uppercase tracking-wider">Done for You Onboarding</div>
          <div className="grid grid-cols-2 gap-3">
            <DInput label="One-time onboarding price ($)" type="number" value={f.onboardingPrice} onChange={set("onboardingPrice")} testid="plan-onboarding-price" />
            <DSelect
              label="Onboarding intake form"
              value={f.onboardingIntakeFormId}
              onChange={set("onboardingIntakeFormId")}
              testid="plan-onboarding-intake-form"
              options={[["", "— None —"], ...forms.map((form) => [form.id, form.name])]}
            />
          </div>
          <p className="text-[10.5px] text-slate-500 mt-1.5">
            Set a price above $0 to make this plan appear in the "Done for You Onboarding" popup on the company Dashboard.
            The selected form (built in CRM → Leads → Forms) is shown after a successful purchase.
          </p>
        </div>

        {/* Module selection — what this plan unlocks */}
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Included features &amp; pages</label>
          <div className="rounded-lg border border-white/10 bg-[#0F1623] p-3 space-y-3 max-h-56 overflow-y-auto" data-testid="plan-modules">
            <div>
              <div className="text-[10.5px] uppercase tracking-wide text-slate-500 mb-1">Always included</div>
              <div className="flex flex-wrap gap-1.5">
                {(catalog.core || []).map((k) => (
                  <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">{k.replace(/_/g, " ")}</span>
                ))}
              </div>
            </div>
            {Object.entries(groups).map(([groupName, mods]) => (
              <div key={groupName}>
                <div className="text-[10.5px] uppercase tracking-wide text-slate-500 mb-1">{groupName}</div>
                <div className="space-y-1">
                  {mods.map((m) => (
                    <label key={m.key} className="flex items-start gap-2 px-1.5 py-1 rounded hover:bg-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={f.modules.includes(m.key)}
                        onChange={() => toggleModule(m.key)}
                        data-testid={`plan-module-${m.key}`}
                        className="mt-0.5 h-3.5 w-3.5"
                      />
                      <span>
                        <span className="text-[13px] text-slate-200 font-medium">{m.label}</span>
                        <span className="block text-[11px] text-slate-500">{m.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI economics */}
        <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] p-3">
          <div className="text-[11.5px] font-semibold text-cyan-200 mb-2 uppercase tracking-wider flex items-center gap-1.5">AI usage billing</div>
          <div className="grid grid-cols-2 gap-3">
            <DInput label="AI included ($/mo)" type="number" value={f.aiIncludedUsd} onChange={set("aiIncludedUsd")} testid="plan-ai-included" />
            <DInput label="Markup on overage (%)" type="number" value={f.aiMarkupPct} onChange={set("aiMarkupPct")} testid="plan-ai-markup" />
          </div>
          <p className="text-[10.5px] text-slate-500 mt-1.5">Billable = max(0, your cost − included) × (1 + markup%). Tune to stay profitable.</p>
        </div>

        <div>
          <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Marketing bullet points (one per line)</label>
          <textarea value={f.features} onChange={set("features")} rows={3} data-testid="plan-features"
            className="w-full px-3 py-2 rounded-lg bg-[#0F1623] border border-white/10 text-[13px] text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30" />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-slate-300">
          <input type="checkbox" checked={f.active} data-testid="plan-active" onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))} /> Active (available for signup)
        </label>
        {error && <div className="px-3 py-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-[12.5px] text-rose-300">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] text-slate-300 hover:bg-white/5">Cancel</button>
          <button type="submit" disabled={busy} data-testid="plan-submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-white text-[13px] font-semibold disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
